import React from 'react';
import { Modal, Descriptions, Tag, Typography, Space } from 'antd';
import { CheckCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatVND } from '../../utils/formatters';

const { Text, Title } = Typography;

interface WorkRecordDetailModalProps {
  visible: boolean;
  workRecord: any;
  onClose: () => void;
}

const WorkRecordDetailModal: React.FC<WorkRecordDetailModalProps> = ({
  visible,
  workRecord,
  onClose
}) => {
  if (!workRecord) return null;

  const formatDate = (date: string) => {
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  };

  const formatDateOnly = (date: string) => {
    return dayjs(date).format('DD/MM/YYYY');
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <span>Chi tiết tác nghiệp</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Thông tin tác nghiệp */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px', color: '#262626' }}>
            Thông tin tác nghiệp
          </Title>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Mã tài sản">
              <Text strong style={{ color: '#1890ff' }}>
                {workRecord.asset_code}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tên tài sản">
              <Text strong>{workRecord.asset_name}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Loại tác nghiệp">
              <Text>{workRecord.maintenance_type_name}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái">
              <Tag color="success">✅ Hoàn thành</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Ngày hoàn thành">
              {workRecord.completed_date ? formatDateOnly(workRecord.completed_date) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Chi phí thực tế">
              <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                {workRecord.cost ? formatVND(workRecord.cost) : '—'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Mã hồ sơ tác nghiệp">
              {workRecord.work_documents ? (
                <Space>
                  <FileTextOutlined style={{ color: '#1890ff' }} />
                  <Text style={{ color: '#1890ff' }} strong>
                    {workRecord.work_documents}
                  </Text>
                </Space>
              ) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Người thực hiện">
              {workRecord.employee_name ? (
                <Text style={{ color: '#1890ff' }} strong>
                  👤 {workRecord.employee_name}
                </Text>
              ) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Nhà cung cấp thực hiện">
              {workRecord.supplier_name ? (
                <Text style={{ color: '#1890ff' }} strong>
                  🏢 {workRecord.supplier_name}
                </Text>
              ) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày tạo">
              {workRecord.work_date ? formatDateOnly(workRecord.work_date) : '—'}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Mô tả và ghi chú */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px', color: '#262626' }}>
            Chi tiết công việc
          </Title>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Mô tả công việc">
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                maxHeight: '120px', 
                overflowY: 'auto',
                padding: '12px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #d9d9d9'
              }}>
                {workRecord.work_description || '—'}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Ghi chú bổ sung">
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                maxHeight: '80px', 
                overflowY: 'auto',
                padding: '12px',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #d9d9d9'
              }}>
                {workRecord.notes || '—'}
              </div>
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Thông tin hệ thống */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px', color: '#262626' }}>
            Thông tin hệ thống
          </Title>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="ID tác nghiệp">
              <Text code>#{workRecord.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="ID chiến dịch">
              <Text code>#{workRecord.maintenance_campaign_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Cập nhật lần cuối">
              {workRecord.updated_at ? formatDateOnly(workRecord.updated_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày tạo">
              {workRecord.created_at ? formatDateOnly(workRecord.created_at) : '—'}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>
    </Modal>
  );
};

export default WorkRecordDetailModal; 